import { createAsyncThunk } from "@reduxjs/toolkit";
import get from "lodash/get";
import { setErrorSnackBarMessage } from "../systemSlice";
import { errorToHandler } from "../../api/error";
import { RootState } from "../../app/store";
import {
  BucketsTotal,
  CatBucketBucketStat,
  SearchObjectsResult,
} from "./catalogSlice";
import { getBytes } from "../../common/utils";
import { DateTime } from "luxon";
import { findUrlParts } from "../../api/api.utils";
import { catRpc } from "../../api";

const catalogRequest = async (
  siteId: string,
  resourceID: string,
  query: string,
) => {
  const baseUrl = `/api/route/${siteId}/catalog/${resourceID}/graphql/v1/`;
  const data = { query };

  const queryRequest = await fetch(baseUrl, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return queryRequest.json();
};

export const fetchBucketsMetrics = createAsyncThunk(
  "catalog/fetchClusterMetrics",
  async (_, { rejectWithValue, dispatch, getState }) => {
    const urlParts = findUrlParts(window.location.href.split("/"), [
      "sites",
      "resource",
    ]);

    const siteId = urlParts[0];
    const resourceID = urlParts[1];

    const query =
      "{  countObjects {  buckets {   bucket  objects totalSize   objectVersions  }   }   }";

    return catalogRequest(siteId, resourceID, query)
      .then((data) => {
        const buckets = get(data, "data.countObjects.buckets", []);

        if (Array.isArray(buckets)) {
          return buckets.map((bucketItem) => {
            const bucket = get(bucketItem, "bucket", "");
            const objects = get(bucketItem, "objects", 0);
            const totalSize = get(bucketItem, "totalSize", 0);
            const objectVersions = get(bucketItem, "objectVersions", 0);

            const dt: CatBucketBucketStat = {
              bucket,
              objects,
              objectVersions,
              totalSize,
            };

            return dt;
          });
        }

        throw new Error("Catalog response is not in the correct format");
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchSearchObject = createAsyncThunk(
  "catalog/fetchObjectSearch",
  async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState() as RootState;

    const urlParts = findUrlParts(window.location.href.split("/"), [
      "sites",
      "resource",
    ]);

    const siteId = urlParts[0];
    const resourceID = urlParts[1];
    const bucketPattern = state.catalog.searchObject.bucketPattern;
    const prefix = state.catalog.searchObject.prefix;
    const size = state.catalog.searchObject.sizeLimit;
    const sizeComparator = state.catalog.searchObject.sizeComparator;
    const time = state.catalog.searchObject.time;
    const timeComparator = state.catalog.searchObject.timeComparator;
    const timeUnit = state.catalog.searchObject.timeUnit;
    const sizeUnit = state.catalog.searchObject.sizeUnit;

    let extraSearchItems = ``;

    if (prefix !== "") {
      extraSearchItems = `${extraSearchItems}, objectPattern: "${prefix}"`;
    }

    if (sizeComparator !== "all") {
      const sizeConversion = getBytes(`${size}`, sizeUnit, true);
      switch (sizeComparator) {
        case "lessthan":
          extraSearchItems = `${extraSearchItems}, sizeLt: "${sizeConversion}"`;
          break;
        case "equal":
          extraSearchItems = `${extraSearchItems}, sizeEq: "${sizeConversion}"`;
          break;
        case "greaterequal":
          extraSearchItems = `${extraSearchItems}, sizeGte: "${sizeConversion}"`;
          break;
        case "greaterthan":
          extraSearchItems = `${extraSearchItems}, sizeGt: "${sizeConversion}"`;
          break;
        case "lessequal":
          extraSearchItems = `${extraSearchItems}, sizeLte: "${sizeConversion}"`;
          break;
      }
    }

    if (timeComparator !== "all") {
      switch (timeComparator) {
        case "lessthan":
          extraSearchItems = `${extraSearchItems}, modTimeLt: "${time} ${timeUnit}"`;
          break;
        case "equal":
          extraSearchItems = `${extraSearchItems}, modTimeEq: "${time} ${timeUnit}"`;
          break;
        case "greaterequal":
          extraSearchItems = `${extraSearchItems}, modTimeGte: "${time} ${timeUnit}"`;
          break;
        case "greaterthan":
          extraSearchItems = `${extraSearchItems}, modTimeGt: "${time} ${timeUnit}"`;
          break;
        case "lessequal":
          extraSearchItems = `${extraSearchItems}, modTimeLte: "${time} ${timeUnit}"`;
          break;
      }
    }

    const query = `{  searchObjects (bucketPattern: "${bucketPattern}"${extraSearchItems})  {   items { bucket  key versionId   lastModified    etag    size    storageClass    userMetadata    isDeleteMarker  }   }   }`;

    return catalogRequest(siteId, resourceID, query)
      .then((data) => {
        const items = get(data, "data.searchObjects.items", []);

        if (Array.isArray(items)) {
          return items.map((objectItem) => {
            const bucket = get(objectItem, "bucket", "");
            const key = get(objectItem, "key", 0);
            const lastModified = get(
              objectItem,
              "lastModified",
              DateTime.now().toISO(),
            );
            const versionId = get(objectItem, "versionId", "null");
            const etag = get(objectItem, "etag", "");
            const size = get(objectItem, "size", 0);
            const storageClass = get(objectItem, "storageClass", "");
            const userMetadata = get(objectItem, "userMetadata", "");
            const isDeleteMarker = get(objectItem, "isDeleteMarker", false);

            const dt: SearchObjectsResult = {
              bucket,
              key,
              lastModified,
              versionId,
              etag,
              size,
              storageClass,
              userMetadata,
              isDeleteMarker,
            };

            return dt;
          });
        }

        throw new Error("Catalog Search result is not in the correct format");
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchCountObject = createAsyncThunk(
  "catalog/fetchObjectCount",
  async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState() as RootState;

    const urlParts = findUrlParts(window.location.href.split("/"), [
      "sites",
      "resource",
    ]);

    const siteId = urlParts[0];
    const resourceID = urlParts[1];
    const bucketPattern = state.catalog.countObject.bucketPattern;
    const prefix = state.catalog.countObject.prefix;
    const size = state.catalog.countObject.sizeLimit;
    const sizeComparator = state.catalog.countObject.sizeComparator;
    const time = state.catalog.countObject.time;
    const timeComparator = state.catalog.countObject.timeComparator;
    const timeUnit = state.catalog.countObject.timeUnit;
    const sizeUnit = state.catalog.countObject.sizeUnit;

    let extraSearchItems = ``;

    if (prefix !== "") {
      extraSearchItems = `${extraSearchItems}, objectPattern: "${prefix}"`;
    }

    if (sizeComparator !== "all") {
      const sizeConversion = getBytes(`${size}`, sizeUnit, true);
      switch (sizeComparator) {
        case "lessthan":
          extraSearchItems = `${extraSearchItems}, sizeLt: "${sizeConversion}"`;
          break;
        case "equal":
          extraSearchItems = `${extraSearchItems}, sizeEq: "${sizeConversion}"`;
          break;
        case "greaterequal":
          extraSearchItems = `${extraSearchItems}, sizeGte: "${sizeConversion}"`;
          break;
        case "greaterthan":
          extraSearchItems = `${extraSearchItems}, sizeGt: "${sizeConversion}"`;
          break;
        case "lessequal":
          extraSearchItems = `${extraSearchItems}, sizeLte: "${sizeConversion}"`;
          break;
      }
    }

    if (timeComparator !== "all") {
      switch (timeComparator) {
        case "lessthan":
          extraSearchItems = `${extraSearchItems}, modTimeLt: "${time} ${timeUnit}"`;
          break;
        case "equal":
          extraSearchItems = `${extraSearchItems}, modTimeEq: "${time} ${timeUnit}"`;
          break;
        case "greaterequal":
          extraSearchItems = `${extraSearchItems}, modTimeGte: "${time} ${timeUnit}"`;
          break;
        case "greaterthan":
          extraSearchItems = `${extraSearchItems}, modTimeGt: "${time} ${timeUnit}"`;
          break;
        case "lessequal":
          extraSearchItems = `${extraSearchItems}, modTimeLte: "${time} ${timeUnit}"`;
          break;
      }
    }

    const query = `{  countObjects (bucketPattern: "${bucketPattern}"${extraSearchItems})  {   totals   {   objects objectVersions  totalSize   }   buckets {   bucket objects objectVersions  totalSize   }   }   }`;

    return catalogRequest(siteId, resourceID, query)
      .then((data) => {
        const totalObjects = get(data, "data.countObjects.totals.objects", 0);
        const objectVersions = get(
          data,
          "data.countObjects.totals.objectVersions",
          0,
        );
        const totalSize = get(data, "data.countObjects.totals.totalSize", 0);
        const buckets = get(data, "data.countObjects.buckets", []);

        const retBuckets: BucketsTotal[] = buckets.map(
          (item: BucketsTotal) => ({
            bucket: get(item, "bucket", ""),
            objects: get(item, "objects", 0),
            objectVersions: get(item, "objectVersions", 0),
            totalSize: get(item, "totalSize", 0),
          }),
        );

        return {
          activeSearch: true,
          totals: {
            objects: totalObjects,
            totalSize,
            objectVersions,
          },
          buckets: retBuckets,
        };
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchCatalogDetails = createAsyncThunk(
  "catalog/fetchCatalogDetails",
  async (
    {
      siteID,
      namespace,
      resourceID,
    }: { siteID: string; namespace: string; resourceID: string },
    { rejectWithValue, dispatch },
  ) => {
    return catRpc.api
      .getCatalog(siteID, namespace, resourceID)
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteCatalogInstance = createAsyncThunk(
  "catalog/deleteCatalogInstance",
  async (
    requestElements: { siteID: string; namespace: string; resourceID: string },
    { rejectWithValue, dispatch },
  ) => {
    return catRpc.api
      .deleteCatalog(
        requestElements.siteID,
        requestElements.namespace,
        requestElements.resourceID,
      )
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);
